/**
 * Plain-English labels for USD wallet ledger rows (non-technical admin UX).
 */

import {
  displayBadgeClass,
  pickUsdLedgerDisplay,
  type UsdLedgerDisplayAudit,
  type UsdLedgerDisplayCategory,
  type UsdLedgerDisplayLink,
} from '../../../types/usdLedgerDisplay';

export type LedgerMetadata = Record<string, unknown>;

export type LedgerUxKind =
  | 'transferring'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'awaiting_approval'
  | 'deposit'
  | 'reversal'
  | 'adjustment'
  | 'generic';

export type LedgerUxStatus = {
  kind: LedgerUxKind;
  label: string;
  shortLabel: string;
  badgeClass: string;
  headerBg: string;
  headerText: string;
};

export type AdminActionBanner = {
  tone: 'info' | 'warning' | 'success' | 'error';
  title: string;
  body: string;
};

export type UsdLedgerRowLike = Record<string, unknown> & { id?: string };

const NETWORK_LABELS: Record<string, string> = {
  btc: 'Bitcoin',
  bitcoin: 'Bitcoin',
  ltc: 'Litecoin',
  litecoin: 'Litecoin',
  doge: 'Dogecoin',
  dogecoin: 'Dogecoin',
  eth: 'Ethereum',
  ethereum: 'Ethereum',
  usdt: 'USDT',
  pyusd: 'PayPal USD',
};

export function parseLedgerMetadata(row: UsdLedgerRowLike | null | undefined): LedgerMetadata {
  const raw = row?.metadata ?? (row as Record<string, unknown> | undefined)?.Metadata;
  return raw && typeof raw === 'object' ? (raw as LedgerMetadata) : {};
}

export function isAutoPayoutMetadata(meta: LedgerMetadata): boolean {
  if (meta.autoPayout === true) return true;
  const payout = meta.payout;
  if (payout && typeof payout === 'object' && (payout as LedgerMetadata).autoPayout === true) return true;
  return false;
}

export function isPortalUsdWithdrawalReference(referenceType: string): boolean {
  return referenceType.trim().toLowerCase() === 'usd_withdrawal_request';
}

export function isUsdOutflowLedgerType(type: string): boolean {
  const t = type.trim().toLowerCase();
  return t === 'withdrawal' || t === 'purchase_credits';
}

export function canManualApproveWithdrawal(row: UsdLedgerRowLike): boolean {
  const type = String(row.type ?? '').trim().toLowerCase();
  const status = String(row.status ?? '').trim().toLowerCase();
  if (type !== 'withdrawal' || status !== 'pending') return false;
  const meta = parseLedgerMetadata(row);
  return !isAutoPayoutMetadata(meta);
}

export function formatPayoutNetwork(network: string): string {
  const k = network.trim().toLowerCase();
  if (!k) return '';
  return NETWORK_LABELS[k] || k.toUpperCase();
}

function payoutPhase(meta: LedgerMetadata): string {
  const phase = String(meta.payoutPhase ?? '').trim().toLowerCase();
  if (phase) return phase;
  const payout = meta.payout;
  if (payout && typeof payout === 'object') {
    return String((payout as LedgerMetadata).payoutPhase ?? '').trim().toLowerCase();
  }
  return '';
}

export function getLedgerUxStatusFromRow(row: UsdLedgerRowLike): LedgerUxStatus {
  const type = String(row.type ?? '').trim().toLowerCase();
  const status = String(row.status ?? '').trim().toLowerCase();
  const refType = String(row.referenceType ?? row.reference_type ?? '').trim().toLowerCase();
  const meta = parseLedgerMetadata(row);
  const { displayLabel, displayCategory } = pickUsdLedgerDisplay(row as Record<string, unknown>);
  return getLedgerUxStatus({
    type,
    status,
    referenceType: refType,
    metadata: meta,
    displayLabel,
    displayCategory,
  });
}

export function getLedgerUxStatus(params: {
  type: string;
  status: string;
  referenceType?: string;
  metadata?: LedgerMetadata;
  displayLabel?: string | null;
  displayCategory?: UsdLedgerDisplayCategory | null;
}): LedgerUxStatus {
  const type = params.type.trim().toLowerCase();
  const status = params.status.trim().toLowerCase();
  const refType = String(params.referenceType ?? '').trim().toLowerCase();
  const meta = params.metadata ?? {};
  const auto = isAutoPayoutMetadata(meta);
  const phase = payoutPhase(meta);
  const portalWithdrawal = type === 'withdrawal' && isPortalUsdWithdrawalReference(refType);

  if (type === 'deposit') {
    return ux('deposit', 'Money in', 'Deposit', emeraldBadge());
  }
  if (type === 'reversal' || type === 'adjustment') {
    if (params.displayLabel) {
      const badgeClass = displayBadgeClass(params.displayCategory ?? null);
      return ux(
        type === 'reversal' ? 'reversal' : 'adjustment',
        params.displayLabel,
        params.displayLabel,
        customBadge(badgeClass),
      );
    }
    if (type === 'reversal') {
      return ux('reversal', 'Transaction Reversal', 'Transaction Reversal', indigoBadge());
    }
    return ux('adjustment', 'Balance Correction — Debit', 'Balance Correction — Debit', slateBadge());
  }

  if (type === 'purchase_credits') {
    if (status === 'completed') {
      return ux('completed', 'Purchase credits', 'Credits', emeraldBadge());
    }
    if (status === 'failed') return ux('failed', 'Unsuccessful', 'Failed', roseBadge());
    if (status === 'cancelled') return ux('cancelled', 'Cancelled', 'Cancelled', roseBadge());
    if (status === 'pending') return ux('generic', 'In progress', 'Pending', amberBadge(true));
  }

  if (type === 'withdrawal' && portalWithdrawal && auto) {
    if (status === 'completed' || phase === 'confirmed') {
      return ux('completed', 'Completed', 'Done', emeraldBadge());
    }
    if (status === 'failed' || phase === 'failed') {
      return ux('failed', 'Unsuccessful (refunded)', 'Failed', roseBadge());
    }
    if (status === 'cancelled') {
      return ux('cancelled', 'Cancelled', 'Cancelled', roseBadge());
    }
    return ux('transferring', 'Sending money', 'Sending', amberBadge(true));
  }

  if (type === 'withdrawal' && status === 'pending' && !auto) {
    return ux('awaiting_approval', 'Needs approval', 'Review', violetBadge());
  }

  if (status === 'completed') {
    return ux('completed', 'Completed', 'Done', emeraldBadge());
  }
  if (status === 'failed') {
    return ux('failed', 'Unsuccessful', 'Failed', roseBadge());
  }
  if (status === 'cancelled') {
    return ux('cancelled', 'Cancelled', 'Cancelled', roseBadge());
  }
  if (status === 'pending') {
    return ux('generic', 'In progress', 'Pending', amberBadge(true));
  }

  const fallback = status ? status.charAt(0).toUpperCase() + status.slice(1) : '—';
  return ux('generic', fallback, fallback, slateBadge());
}

function ux(
  kind: LedgerUxKind,
  label: string,
  shortLabel: string,
  colors: { badgeClass: string; headerBg: string; headerText: string },
): LedgerUxStatus {
  return { kind, label, shortLabel, ...colors };
}

function emeraldBadge() {
  return {
    badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    headerBg: '#dcfce7',
    headerText: '#166534',
  };
}

function amberBadge(pulse = false) {
  return {
    badgeClass: `border-amber-200 bg-amber-50 text-amber-900${pulse ? ' animate-pulse' : ''}`,
    headerBg: '#fef3c7',
    headerText: '#92400e',
  };
}

function roseBadge() {
  return {
    badgeClass: 'border-rose-200 bg-rose-50 text-rose-800',
    headerBg: '#ffe4e6',
    headerText: '#9f1239',
  };
}

function violetBadge() {
  return {
    badgeClass: 'border-violet-200 bg-violet-50 text-violet-900',
    headerBg: '#ede9fe',
    headerText: '#5b21b6',
  };
}

function slateBadge() {
  return {
    badgeClass: 'border-slate-200 bg-slate-50 text-slate-700',
    headerBg: 'hsl(var(--muted))',
    headerText: 'hsl(var(--muted-foreground))',
  };
}

function indigoBadge() {
  return {
    badgeClass: 'border-indigo-200 bg-indigo-50 text-indigo-800',
    headerBg: '#e0e7ff',
    headerText: '#3730a3',
  };
}

function customBadge(badgeClass: string) {
  return {
    badgeClass,
    headerBg: 'hsl(var(--muted))',
    headerText: 'hsl(var(--foreground))',
  };
}

export function getAdminActionBanner(params: {
  type: string;
  status: string;
  referenceType?: string;
  metadata?: LedgerMetadata;
}): AdminActionBanner | null {
  const type = params.type.trim().toLowerCase();
  const status = params.status.trim().toLowerCase();
  const refType = String(params.referenceType ?? '').trim().toLowerCase();
  const meta = params.metadata ?? {};
  const ux = getLedgerUxStatus({ type, status, referenceType: refType, metadata: meta });

  if (type === 'withdrawal' && isPortalUsdWithdrawalReference(refType) && isAutoPayoutMetadata(meta)) {
    if (ux.kind === 'transferring') {
      return {
        tone: 'info',
        title: 'No action needed',
        body:
          'The system is sending funds to the registered payout wallet. This usually takes a few minutes to a few hours depending on the network. Do not use Approve or Reject.',
      };
    }
    if (ux.kind === 'failed') {
      const err =
        String(
          (meta.payout && typeof meta.payout === 'object'
            ? (meta.payout as LedgerMetadata).errorMessage
            : null) ?? meta.errorMessage ?? '',
        ).trim() || 'The payout provider reported a failure.';
      return {
        tone: 'error',
        title: 'Funds were refunded automatically',
        body: `${err} The USD wallet balance was restored. Do not use Reject — the customer can try again or contact support.`,
      };
    }
  }

  if (ux.kind === 'awaiting_approval') {
    return {
      tone: 'warning',
      title: 'Approval required',
      body: 'Review the payout address and amount, then Approve or Reject this withdrawal.',
    };
  }

  return null;
}

export function buildCardStatusLine(params: {
  type: string;
  status: string;
  referenceType?: string;
  metadata?: LedgerMetadata;
}): string {
  const ux = getLedgerUxStatus(params);
  const meta = params.metadata ?? {};
  const network = formatPayoutNetwork(String(meta.payoutNetwork ?? ''));
  const parts = [ux.label];
  if (network && params.type.trim().toLowerCase() === 'withdrawal') {
    parts.push(network);
  }
  return parts.join(' · ');
}

export function extractPayoutTracking(meta: LedgerMetadata): {
  externalTransactionId: string;
  provider: string;
  errorMessage: string;
} {
  const payout = meta.payout && typeof meta.payout === 'object' ? (meta.payout as LedgerMetadata) : {};
  return {
    externalTransactionId: String(
      payout.externalTransactionId ?? payout.payoutOrderId ?? meta.externalTransactionId ?? '',
    ).trim(),
    provider: String(payout.provider ?? meta.provider ?? '').trim(),
    errorMessage: String(payout.errorMessage ?? meta.errorMessage ?? '').trim(),
  };
}

export type UsdLedgerDisplay = {
  directionLabel: string;
  reasonLabel: string;
  sourceLabel: string;
  providerLabel: string;
  subtitle: string;
  detailSummary: string;
  badgeLabel: string;
  amountLabel: string;
  feeAmount?: number;
  hasFee: boolean;
  isMoneyIn: boolean;
  amountGross: number;
  amountNet: number;
  displayCategory?: UsdLedgerDisplayCategory | null;
  displayLabel?: string | null;
  audit?: UsdLedgerDisplayAudit | null;
  links?: UsdLedgerDisplayLink[];
};

export type BuildUsdLedgerDisplayContext = {
  ownerDisplayName?: string;
};

const PROVIDER_LABELS: Record<string, string> = {
  zeroxprocessing: '0xProcessing',
  btcpay: 'BTCPay',
  linkmepay: 'LinkMePay',
  ggusonepay: 'GGUSOnePay',
  meld: 'Meld',
  shop_player_cash: 'Cash',
  balance_conversion: 'Balance conversion',
  internal: 'Internal',
  admin_settlement_payout: 'Admin settlement',
};

function ledgerNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function ledgerStr(v: unknown): string {
  return v != null ? String(v).trim() : '';
}

export function formatProviderLabel(providerRaw: string): string {
  const k = providerRaw.trim().toLowerCase();
  if (!k) return 'Internal';
  return PROVIDER_LABELS[k] ?? providerRaw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function walletOwnerFallbackSubtitle(row: UsdLedgerRowLike): string {
  const wallet = (row.usdWallet ?? row.usd_wallet) as Record<string, unknown> | undefined;
  const ownerType = ledgerStr(wallet?.ownerType ?? wallet?.owner_type).toLowerCase();
  if (ownerType === 'shop') return 'Shop wallet';
  if (ownerType === 'agent') return 'Agent wallet';
  if (ownerType === 'super_agent') return 'Super agent wallet';
  if (ownerType === 'player') return 'Player wallet';
  if (ownerType === 'platform') return 'Platform wallet';
  if (ownerType) return `${ownerType.replace(/_/g, ' ')} wallet`;
  return 'USD wallet';
}

function formatSignedUsdAmount(amount: number, opts?: { netSuffix?: boolean }): string {
  const v = ledgerNum(amount);
  const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const suffix = opts?.netSuffix ? ' net' : '';
  if (v > 0.0000001) return `+$${abs}${suffix}`;
  if (v < -0.0000001) return `-$${abs}${suffix}`;
  return `$${abs}${suffix}`;
}

function resolveLedgerFee(row: UsdLedgerRowLike, meta: LedgerMetadata): {
  hasFee: boolean;
  feeAmount: number;
  amountGross: number;
  amountNet: number;
} {
  const amountGross = ledgerNum(row.amountGross ?? row.amount_gross);
  const amountNet = ledgerNum(row.amountNet ?? row.amount_net);
  const platformFee = ledgerNum(meta.platformFeeAmount ?? meta.platform_fee_amount);
  const feeFromDelta =
    Number.isFinite(amountGross) && Number.isFinite(amountNet) && Math.abs(amountGross - amountNet) > 0.0000001
      ? Math.abs(amountGross - amountNet)
      : 0;
  const feeAmount = platformFee > 0 ? platformFee : feeFromDelta;
  const hasFee = feeAmount > 0.0000001;
  return { hasFee, feeAmount, amountGross, amountNet };
}

function resolveDirectionLabel(type: string, netDelta: number): { directionLabel: string; isMoneyIn: boolean } {
  const t = type.trim().toLowerCase();
  if (t === 'deposit') return { directionLabel: 'Money in', isMoneyIn: true };
  if (isUsdOutflowLedgerType(t)) return { directionLabel: 'Money out', isMoneyIn: false };
  if (t === 'reversal') return { directionLabel: 'Money in', isMoneyIn: netDelta >= 0 };
  if (t === 'adjustment') {
    return netDelta >= 0
      ? { directionLabel: 'Money in', isMoneyIn: true }
      : { directionLabel: 'Money out', isMoneyIn: false };
  }
  return netDelta >= 0
    ? { directionLabel: 'Money in', isMoneyIn: true }
    : { directionLabel: 'Money out', isMoneyIn: false };
}

function resolveBusinessLabels(params: {
  type: string;
  referenceType: string;
  provider: string;
  meta: LedgerMetadata;
}): { reasonLabel: string; sourceLabel: string; providerLabel: string } {
  const type = params.type.trim().toLowerCase();
  const refType = params.referenceType.trim().toLowerCase();
  const provider = params.provider.trim().toLowerCase();
  const meta = params.meta;
  const metaSource = ledgerStr(meta.source);
  const providerLabel = formatProviderLabel(params.provider);
  const staticWallet =
    meta.staticWallet === true ||
    String(meta.staticWallet ?? '').toLowerCase() === 'true' ||
    metaSource === 'zerox_static_wallet_usd_only';

  if (provider === 'zeroxprocessing' && staticWallet) {
    return {
      reasonLabel: 'Static wallet top-up',
      sourceLabel: '0xProcessing Static Wallet',
      providerLabel: '0xProcessing',
    };
  }

  if (refType === 'payment_transaction' && provider === 'linkmepay') {
    return {
      reasonLabel: type === 'withdrawal' ? 'Gateway payout' : 'Gateway deposit',
      sourceLabel: 'LinkMePay',
      providerLabel: 'LinkMePay',
    };
  }

  if (refType === 'payment_transaction' && provider === 'btcpay') {
    return {
      reasonLabel: type === 'withdrawal' ? 'Player payout' : 'Gateway deposit',
      sourceLabel: 'BTCPay',
      providerLabel: 'BTCPay',
    };
  }

  if (refType === 'payment_transaction' && provider === 'ggusonepay') {
    return {
      reasonLabel: type === 'withdrawal' ? 'Gateway payout' : 'Gateway deposit',
      sourceLabel: 'GGUSOnePay',
      providerLabel: 'GGUSOnePay',
    };
  }

  if (metaSource === 'depositSettlementCoreService.creditPlayerMainWallet') {
    return {
      reasonLabel: 'Player deposit credited to tier wallet',
      sourceLabel: providerLabel,
      providerLabel,
    };
  }

  if (metaSource.startsWith('depositSettlementCoreService.settleCdnDepositFromPaymentTx')) {
    return {
      reasonLabel: 'Credit purchase deposit',
      sourceLabel: providerLabel,
      providerLabel,
    };
  }

  if (provider === 'shop_player_cash') {
    return { reasonLabel: 'Cash credit sale', sourceLabel: 'Cash', providerLabel: 'Cash' };
  }

  if (refType === 'balance_to_credits') {
    return {
      reasonLabel: 'Converted USD to credits',
      sourceLabel: 'Internal balance conversion',
      providerLabel: 'Internal',
    };
  }

  if (refType === 'balance_to_credits_settlement') {
    return {
      reasonLabel: 'Upstream USD settlement',
      sourceLabel: 'Internal settlement',
      providerLabel: 'Internal',
    };
  }

  if (metaSource === 'playerPayoutTierSettlementBizService') {
    return {
      reasonLabel: 'Player payout',
      sourceLabel: providerLabel,
      providerLabel,
    };
  }

  if (provider === 'admin_settlement_payout') {
    return {
      reasonLabel: 'Online debt settlement payout',
      sourceLabel: 'Admin settlement',
      providerLabel: 'Admin settlement',
    };
  }

  if (refType === 'seed') {
    return {
      reasonLabel: 'Initial platform funding',
      sourceLabel: 'Internal seed',
      providerLabel: 'Internal',
    };
  }

  if (refType === 'payment_transaction') {
    return {
      reasonLabel: type === 'withdrawal' ? 'Gateway payout' : 'Gateway deposit',
      sourceLabel: providerLabel,
      providerLabel,
    };
  }

  if (type === 'deposit') {
    return { reasonLabel: 'Wallet deposit', sourceLabel: providerLabel, providerLabel };
  }
  if (type === 'purchase_credits') {
    return { reasonLabel: 'Converted USD to credits', sourceLabel: 'Internal balance conversion', providerLabel: 'Internal' };
  }

  if (type === 'withdrawal') {
    return { reasonLabel: 'Wallet withdrawal', sourceLabel: providerLabel, providerLabel };
  }
  if (type === 'adjustment') {
    return { reasonLabel: 'Balance correction', sourceLabel: providerLabel, providerLabel };
  }
  if (type === 'reversal') {
    return { reasonLabel: 'Transaction reversal', sourceLabel: providerLabel, providerLabel };
  }

  return {
    reasonLabel: 'USD wallet activity',
    sourceLabel: providerLabel,
    providerLabel,
  };
}

export function buildUsdLedgerDisplay(
  row: UsdLedgerRowLike,
  context?: BuildUsdLedgerDisplayContext,
): UsdLedgerDisplay {
  const meta = parseLedgerMetadata(row);
  const type = ledgerStr(row.type).toLowerCase();
  const status = ledgerStr(row.status);
  const referenceType = ledgerStr(row.referenceType ?? row.reference_type);
  const provider = ledgerStr(row.provider ?? meta.provider);
  const balanceNetBefore = ledgerNum(row.balanceNetBefore ?? row.balance_net_before);
  const balanceNetAfter = ledgerNum(row.balanceNetAfter ?? row.balance_net_after);
  const netDelta = balanceNetAfter - balanceNetBefore;

  const { hasFee, feeAmount, amountGross, amountNet } = resolveLedgerFee(row, meta);
  const { directionLabel, isMoneyIn } = resolveDirectionLabel(type, netDelta);
  const { reasonLabel, sourceLabel, providerLabel } = resolveBusinessLabels({
    type,
    referenceType,
    provider,
    meta,
  });
  const { displayLabel, displayCategory, audit, links } = pickUsdLedgerDisplay(
    row as Record<string, unknown>,
  );
  const effectiveReason = audit?.reason?.trim() || reasonLabel;

  const subtitle =
    ledgerStr(context?.ownerDisplayName) || walletOwnerFallbackSubtitle(row);

  const detailParts = [effectiveReason, providerLabel].filter(Boolean);
  const detailSummary = detailParts.join(' · ');

  const ux = getLedgerUxStatus({
    type,
    status,
    referenceType,
    metadata: meta,
    displayLabel,
    displayCategory,
  });

  let displayAmount = amountNet;
  if (isUsdOutflowLedgerType(type) && amountGross > 0) {
    displayAmount = amountGross;
  } else if (!displayAmount && netDelta) {
    displayAmount = Math.abs(netDelta);
  }

  const signedAmount = isMoneyIn ? Math.abs(displayAmount) : -Math.abs(displayAmount || netDelta);
  const amountLabel = formatSignedUsdAmount(signedAmount, { netSuffix: hasFee });

  return {
    directionLabel,
    reasonLabel: effectiveReason,
    sourceLabel,
    providerLabel,
    subtitle,
    detailSummary,
    badgeLabel: ux.shortLabel,
    amountLabel,
    feeAmount: hasFee ? feeAmount : undefined,
    hasFee,
    isMoneyIn,
    amountGross,
    amountNet,
    displayCategory,
    displayLabel,
    audit,
    links,
  };
}

export function buildProviderReferenceLabel(row: UsdLedgerRowLike, meta?: LedgerMetadata): string {
  const m = meta ?? parseLedgerMetadata(row);
  const tracking = extractPayoutTracking(m);
  if (tracking.externalTransactionId) return tracking.externalTransactionId;
  const paymentTx = row.paymentTransaction as Record<string, unknown> | undefined;
  const ext = ledgerStr(paymentTx?.externalTransactionId ?? paymentTx?.external_transaction_id);
  if (ext) return ext;
  const mockSw = ledgerStr(m.staticWalletOrderId ?? m.mockStaticWalletId ?? m.externalOrderId);
  if (mockSw) return mockSw;
  return '';
}

export function buildInternalReferenceLabel(referenceType: string, referenceId: string): string {
  const rt = referenceType.trim().toLowerCase();
  const id = referenceId.trim();
  if (!id) return '';
  if (rt === 'payment_transaction') return `Payment transaction #${id}`;
  if (rt === 'usd_withdrawal_request') return `Withdrawal request #${id}`;
  if (rt === 'balance_to_credits') return `Out · buyer purchase credits #${id}`;
  if (rt === 'balance_to_credits_settlement') return `In · credits settlement #${id}`;
  if (rt) return `${rt.replace(/_/g, ' ')} #${id}`;
  return id;
}

export function buildPortalWithdrawalStory(params: {
  ownerShortName: string;
  requestedNetUsd: number;
  network: string;
  uxKind: LedgerUxKind;
}): string {
  const amount = params.requestedNetUsd;
  const amt = Number.isFinite(amount) && amount > 0 ? `$${amount.toFixed(2)}` : 'funds';
  const dest = params.network ? `${params.network} wallet` : 'external wallet';
  const who = params.ownerShortName || 'This account';

  switch (params.uxKind) {
    case 'transferring':
      return `${who} requested a withdrawal of ${amt}. Their USD balance was reduced and the system is sending money to their ${dest}.`;
    case 'completed':
      return `${amt} was sent to ${who}'s ${dest}. This withdrawal is complete.`;
    case 'failed':
      return `The payout could not be completed. ${amt} was returned to ${who}'s USD wallet.`;
    case 'cancelled':
      return `This withdrawal was cancelled. ${who}'s USD wallet was adjusted accordingly.`;
    default:
      return `${who}'s USD wallet balance changed by ${amt} for a withdrawal.`;
  }
}
